/**
 * Optimistic-reaction coordinator (#989).
 *
 * Two things ride the adaptive 30s messages poll, and both are handled here:
 *
 * 1. POLL RACE (feature doc §4.3): an in-flight poll can land between a toggle's
 *    optimistic write and the server ack and clobber the chip with a stale
 *    aggregation. Defense: while a toggle for a key is unsettled we record its
 *    desired end-state (`mine`); the messages `queryFn` runs every poll response
 *    through {@link overlayPendingReactions}, which re-derives the viewer's
 *    toggle on top of whatever the server returned. Idempotent (targets a
 *    desired end-state, not a delta) → same reference when already correct.
 *
 * 2. COMPLETION-ORDER RACE (Frank r3): the same (roomId, messageId, emoji) can
 *    be toggled from two independent mutation instances (chip row + action-sheet
 *    palette). Firing one HTTP request per toggle let them COMPLETE out of order
 *    on the server — a slow add landing after a fast remove leaves the backend
 *    reacted, and the next poll reverses the user's remove. Fix: SERIALIZE per
 *    key on the client and COALESCE intermediate flips. At most one request is
 *    ever in flight per key; a concurrent toggle only updates the desired
 *    end-state, and the running request chain fires exactly one follow-up when
 *    the server hasn't converged to the latest desired. So A→B→A while in flight
 *    nets zero follow-ups, A→B nets one, and the server always converges to the
 *    LAST CLIENT INTENT. Single-toggle latency is unchanged (no chain running →
 *    fire immediately). This retires the out-of-order hazard entirely and makes
 *    the poll overlay + serialization the ONLY two mechanisms (the r2/r3
 *    generation-token machinery is now redundant and removed).
 *
 * One module-level per-key state carries both: `desiredMine` (for the overlay +
 * the coalescing target) and `inFlight` (the single-request-chain lock). It is
 * created on toggle and deleted the instant the chain converges (atomic with the
 * convergence check) so the overlay stops once the server is authoritative and
 * matches. Pure + transport-clean (no React) — shared by the separate toggle and
 * messages-query hook instances. Tests reset it via {@link __resetPendingReactions}.
 */

import { REACTION_PALETTE, type MessageReaction, type ReactionEmoji } from './api/messages';

const PALETTE_RANK: Record<string, number> = Object.fromEntries(
  REACTION_PALETTE.map((emoji, index) => [emoji, index]),
);

function sortByPalette(reactions: MessageReaction[]): MessageReaction[] {
  return [...reactions].sort(
    (a, b) => (PALETTE_RANK[a.emoji] ?? 0) - (PALETTE_RANK[b.emoji] ?? 0),
  );
}

/**
 * Return a reactions array reflecting `desiredMine` for `emoji`, IDEMPOTENTLY.
 * If the input already matches the desired state the SAME reference is returned
 * (so callers can cheaply skip a re-render). Never mutates the input.
 *
 * - desiredMine=true, not currently mine  → count+1, mine=true (add the group
 *   palette-ordered if this emoji had no reactions yet).
 * - desiredMine=false, currently mine     → count-1, mine=false (drop the group
 *   entirely when its count reaches 0).
 */
export function applyDesiredMine(
  reactions: MessageReaction[],
  emoji: ReactionEmoji,
  desiredMine: boolean,
): MessageReaction[] {
  const existing = reactions.find((r) => r.emoji === emoji);
  const currentMine = existing?.mine ?? false;
  if (currentMine === desiredMine) return reactions; // idempotent no-op

  if (desiredMine) {
    if (existing) {
      return sortByPalette(
        reactions.map((r) =>
          r.emoji === emoji ? { ...r, count: r.count + 1, mine: true } : r,
        ),
      );
    }
    return sortByPalette([...reactions, { emoji, count: 1, mine: true }]);
  }

  // desiredMine === false — remove my reaction, dropping an emptied group.
  return sortByPalette(
    reactions
      .map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, mine: false } : r))
      .filter((r) => r.count > 0),
  );
}

/**
 * Per-key toggle state:
 *  - `desiredMine`: the LATEST requested end-state for the key (every toggle
 *    updates it). Drives both the poll overlay and the coalescing target.
 *  - `inFlight`: true while one request chain is already on the wire for the key
 *    (the serialization lock — a concurrent toggle coalesces rather than firing
 *    a second request).
 */
interface KeyToggleState {
  desiredMine: boolean;
  inFlight: boolean;
}

// roomId → messageId → (emoji → state). Present only while a toggle for the key
// is unsettled; deleted on convergence/error.
const toggleStates = new Map<string, Map<string, Map<ReactionEmoji, KeyToggleState>>>();

function stateFor(
  roomId: string,
  messageId: string,
  emoji: ReactionEmoji,
): KeyToggleState | undefined {
  return toggleStates.get(roomId)?.get(messageId)?.get(emoji);
}

/**
 * Record the latest desired end-state for the key (called from `onMutate` on
 * every toggle, synchronously). Creates the state if absent (`inFlight` starts
 * false — the mutation's request chain claims it via {@link claimToggleChain}).
 * The messages `queryFn` overlay re-applies this on every poll until the state
 * is deleted at convergence.
 */
export function setDesiredMine(
  roomId: string,
  messageId: string,
  emoji: ReactionEmoji,
  desiredMine: boolean,
): void {
  let byMessage = toggleStates.get(roomId);
  if (!byMessage) {
    byMessage = new Map();
    toggleStates.set(roomId, byMessage);
  }
  let byEmoji = byMessage.get(messageId);
  if (!byEmoji) {
    byEmoji = new Map();
    byMessage.set(messageId, byEmoji);
  }
  const s = byEmoji.get(emoji);
  if (s) s.desiredMine = desiredMine;
  else byEmoji.set(emoji, { desiredMine, inFlight: false });
}

/** The latest desired end-state for the key, or `undefined` if none is pending. */
export function getDesiredMine(
  roomId: string,
  messageId: string,
  emoji: ReactionEmoji,
): boolean | undefined {
  return stateFor(roomId, messageId, emoji)?.desiredMine;
}

/**
 * Claim the single in-flight request slot for the key. Returns `true` if the
 * caller now owns the request chain (it should fire), `false` if a chain is
 * already running (COALESCE — the running chain will converge to the desired
 * state this caller already recorded via {@link setDesiredMine}).
 */
export function claimToggleChain(
  roomId: string,
  messageId: string,
  emoji: ReactionEmoji,
): boolean {
  const s = stateFor(roomId, messageId, emoji);
  if (!s || s.inFlight) return false;
  s.inFlight = true;
  return true;
}

/**
 * Delete the key's toggle state (the request chain converged or errored). Callers
 * MUST invoke this synchronously with the convergence decision (no await between)
 * so a coalescing toggle can't slip a new intent into a slot about to be dropped.
 * Prunes empty maps.
 */
export function endToggleChain(
  roomId: string,
  messageId: string,
  emoji: ReactionEmoji,
): void {
  const byMessage = toggleStates.get(roomId);
  const byEmoji = byMessage?.get(messageId);
  if (!byEmoji) return;
  byEmoji.delete(emoji);
  if (byEmoji.size === 0) byMessage!.delete(messageId);
  if (byMessage!.size === 0) toggleStates.delete(roomId);
}

/**
 * Overlay any unsettled toggles for `message` on top of its server reactions.
 * Returns the SAME message reference when nothing is pending (or the desired
 * state already matches) so the poll's structural sharing is preserved.
 */
export function overlayPendingReactions<
  T extends { id: string; reactions?: MessageReaction[] },
>(roomId: string, message: T): T {
  const byEmoji = toggleStates.get(roomId)?.get(message.id);
  if (!byEmoji || byEmoji.size === 0) return message;

  let reactions = message.reactions ?? [];
  let changed = false;
  for (const [emoji, state] of byEmoji) {
    const next = applyDesiredMine(reactions, emoji, state.desiredMine);
    if (next !== reactions) {
      reactions = next;
      changed = true;
    }
  }
  if (!changed) return message;
  return { ...message, reactions };
}

/** Test-only: wipe all module state so it can't leak across tests. */
export function __resetPendingReactions(): void {
  toggleStates.clear();
}
